// TAA Tilts: an analyst book against DS70's CURRENT approved set in the trading app
// (the mandate checker's baseline) — current tilts, the same at each saved change and
// today, the growth tilt, and the book's active / passive split. Laid out like the
// Portfolio Viewer's tilt charts.
import React from 'react';
import {
  BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { API, fmtDate, getAssetClassColor, useApiGet } from './apUtils';
import { BookSelect, Notice } from './apComponents';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const signed = (v, dp = 2) => `${v >= 0 ? '+' : ''}${v.toFixed(dp)}%`;

export default function TaaTiltsTab({ code, setCode, books }) {
  const { data, error, loading } = useApiGet(code ? `${API}/portfolios/${code}/taa-tilts` : null);
  const labels = (data?.dates || []).map(fmtDate);
  const latestKeys = Object.keys(data?.latest || {})
    .sort((a, b) => Math.abs(data.latest[b]) - Math.abs(data.latest[a]));
  const exp = data?.exposure;
  const base = data?.baseline;
  const baseText = base ? `DS70's current approved set (v${base.version}, effective ${fmtDate(base.effective_from)})` : 'DS70';

  return (
    <div className="tab-panel">
      <BookSelect id="ap-tilts-book" value={code} onChange={setCode} books={books} />
      {error && <Notice kind="error">Failed to load: {error}</Notice>}
      {loading && <div className="chart-loading">Loading TAA tilts...</div>}
      {data && Object.keys(data.unmapped_sacs || {}).length > 0 && (
        <Notice kind="warn">
          Sub-asset classes with no broad class in Investment Config (left out of the tilts):{' '}
          {Object.keys(data.unmapped_sacs).join(', ')}
        </Notice>
      )}
      {base && (base.unclassified > 0 || base.no_growth_exposure?.length > 0) && (
        <Notice kind="warn">
          {base.unclassified > 0 && `${base.unclassified.toFixed(2)}% of DS70's approved set has no sub-asset class. `}
          {base.no_growth_exposure?.length > 0 && `No growth exposure in Investment Config for: ${base.no_growth_exposure.join(', ')}.`}
        </Notice>
      )}
      {data && (
        <div className="tilts-charts-container">
          <div className="tilts-snapshot">
            <h4>Current TAA Tilts</h4>
            <p className="tilts-note">Today&apos;s holdings minus {baseText}, by asset class.</p>
            {latestKeys.length ? (
              <div className="chart-container">
                <Bar
                  data={{
                    labels: latestKeys,
                    datasets: [{
                      label: 'Tilt (%)',
                      data: latestKeys.map((k) => data.latest[k]),
                      backgroundColor: latestKeys.map((k) => (data.latest[k] >= 0 ? '#91CF9B' : '#dc3545')),
                      borderColor: latestKeys.map((k) => (data.latest[k] >= 0 ? '#6db86d' : '#a71d2a')),
                      borderWidth: 1,
                    }],
                  }}
                  options={{
                    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: { callbacks: { label: (c) => `${signed(c.parsed.x)} ${c.parsed.x >= 0 ? '(Overweight vs DS70)' : '(Underweight vs DS70)'}` } },
                    },
                    scales: {
                      x: { grid: { color: '#f0f0f0' }, ticks: { callback: (v) => `${v >= 0 ? '+' : ''}${v}%`, font: { size: 10 } },
                        title: { display: true, text: 'Tilt (Book - DS70 approved set)', font: { size: 11 } } },
                      y: { grid: { display: false }, ticks: { font: { size: 10 } } },
                    },
                  }}
                />
              </div>
            ) : <div className="no-data-message">No current tilt data available</div>}
          </div>

          <div className="tilts-time-series">
            <h4>Historical TAA Tilts</h4>
            <p className="tilts-note">Each saved change and today, all measured against {baseText}. For each date against
              the DS70 set in force at the time, use Allocation History → Relative to DS70.</p>
            {labels.length ? (
              <div className="chart-container">
                <Bar
                  data={{
                    labels,
                    datasets: Object.entries(data.series || {}).map(([ac, values], i) => ({
                      label: ac, data: values, backgroundColor: getAssetClassColor(ac, i),
                      borderColor: getAssetClassColor(ac, i), borderWidth: 0,
                    })),
                  }}
                  options={{
                    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15, font: { size: 11 } } },
                      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${signed(c.parsed.y)}` } },
                    },
                    scales: {
                      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
                      y: { stacked: true, grid: { color: '#f0f0f0' },
                        ticks: { callback: (v) => `${v >= 0 ? '+' : ''}${v}%`, font: { size: 10 } },
                        title: { display: true, text: 'Tilt (Book - DS70 approved set) %', font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            ) : <div className="no-data-message">No historical tilt data available</div>}
          </div>

          <div className="tilts-time-series">
            <h4>Historical Growth Tilt vs DS70</h4>
            <p className="tilts-note">Growth weight (holdings × each sub asset class&apos;s growth exposure) minus the growth of
              {' '}{baseText}{exp ? ` (${exp.ds70_growth.toFixed(2)}%)` : ''}. Above zero = more growth than DS70. The mandate
              limit is ±5.00pp.</p>
            {exp?.dates?.length ? (
              <div className="chart-container">
                <Bar
                  data={{
                    labels,
                    datasets: [{
                      label: 'Growth tilt', data: exp.growth_tilt,
                      backgroundColor: exp.growth_tilt.map((v) => ((v ?? 0) >= 0 ? '#12706C' : '#AA8090')), borderWidth: 0,
                    }],
                  }}
                  options={{
                    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (c) => {
                            const i = c.dataIndex;
                            const out = [
                              `Growth tilt: ${signed(c.parsed.y)}`,
                              `Growth ${exp.growth[i].toFixed(2)}% vs DS70 ${exp.ds70_growth.toFixed(2)}%`,
                              `Defensive ${exp.defensive[i].toFixed(2)}%`,
                            ];
                            if (exp.unclassified[i]) out.push(`Unclassified sub asset class ${exp.unclassified[i].toFixed(2)}%`);
                            return out;
                          },
                        },
                      },
                    },
                    scales: {
                      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                      y: { grid: { color: '#f0f0f0' }, ticks: { callback: (v) => `${v >= 0 ? '+' : ''}${v}%`, font: { size: 10 } },
                        title: { display: true, text: 'Growth tilt (Book - DS70) %', font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            ) : <div className="no-data-message">No growth history available</div>}
          </div>

          <div className="tilts-time-series">
            <h4>Historical Active / Passive Split</h4>
            <p className="tilts-note">Passive = funds with AP List style &quot;Passive&quot; or &quot;Benchmark&quot; (index funds) plus cash;
              active = all other holdings.</p>
            {exp?.dates?.length ? (
              <div className="chart-container">
                <Bar
                  data={{
                    labels,
                    datasets: [
                      { label: 'Active', data: exp.active, backgroundColor: '#012341', borderWidth: 0 },
                      { label: 'Passive (incl. cash)', data: exp.passive, backgroundColor: '#A6C6C1', borderWidth: 0 },
                    ],
                  }}
                  options={{
                    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15, font: { size: 11 } } },
                      tooltip: {
                        callbacks: {
                          label: (c) => `${c.dataset.label}: ${c.parsed.y.toFixed(2)}%`,
                          afterBody: (items) => {
                            const n = exp.not_in_ap_list[items[0]?.dataIndex];
                            return n ? [`(${n.toFixed(2)}% of active not found in AP List)`] : [];
                          },
                        },
                      },
                    },
                    scales: {
                      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
                      y: { stacked: true, grid: { color: '#f0f0f0' }, ticks: { callback: (v) => `${v}%`, font: { size: 10 } },
                        title: { display: true, text: 'Weight %', font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            ) : <div className="no-data-message">No active / passive history available</div>}
          </div>
        </div>
      )}
    </div>
  );
}
